import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import clsx from 'clsx'
import { useMemo } from 'react'
import { HiDotsHorizontal } from 'react-icons/hi'
import { useNavigate } from 'react-router-dom'

import noop from '@utils/noop'

import { Button, ButtonLink } from '@elements/button'
import { Dropdown, DropdownHandler, DropdownMenu } from '@elements/dropdown'

import { SubTableProps, TableClickRow, TableHeaderMeta } from './Table.types'

export const SubTable = (props: SubTableProps) => {
  const {
    rootData,
    data,
    columns,
    access,
    keyId = 'id',
    actions,
    customActions,
    hasAutoNumber = false,
    onClickRow,
    isNoActionDetail = false,
    actionsSubTableProps,
  } = props

  const hasAction = actions?.hasView || actions?.hasEdit || customActions

  const memoizedData = useMemo(() => {
    if (!hasAutoNumber && !hasAction) return data
    return data.map((val: any, idx: number) => ({
      ...val,
      ...(hasAutoNumber && {
        autoNumber: idx + 1,
      }),
    }))
  }, [data])

  const hasEdit = access?.hasUpdate() || false

  const { hasEdit: hasEditBtnProps, hasView: hasViewBtnProps } =
    actionsSubTableProps || {}

  const defaultFnFalse = () => false
  const { hideFn: hideEditBtnFn = defaultFnFalse } = hasEditBtnProps || {}
  const { hideFn: hideViewBtnFn = defaultFnFalse } = hasViewBtnProps || {}

  const memoizedColumns = useMemo(
    () => [
      {
        accessorKey: 'expander',
        header: '',
        enableSorting: false,
        cell: ({ row }: any) => {
          return (
            <div
              style={{
                paddingLeft: `${row.depth * 2}rem`,
              }}
            >
              <div>
                <Button
                  type='button'
                  size='sm'
                  className='p-0'
                  variant='ghost'
                  onClick={noop}
                />
              </div>
            </div>
          )
        },
      },
      ...(hasAutoNumber
        ? [
            {
              accessorKey: 'autoNumber',
              header: 'No',
              meta: { align: 'right' },
              cell: (value: any) => {
                const number = value?.row?.original?.autoNumber ?? ''
                return number ? <div>{number}</div> : null
              },
            },
          ]
        : []),
      ...columns,
      ...(hasAction
        ? [
            {
              header: 'Action',
              cell: (value: any) => {
                const idx = value?.row?.original?.[keyId] || ''
                return (
                  <Dropdown placement='bottom-end'>
                    <DropdownHandler
                      onClick={(event) => {
                        event.stopPropagation()
                      }}
                    >
                      <Button variant='outline' type='button'>
                        <HiDotsHorizontal className='h-5 w-5' />
                      </Button>
                    </DropdownHandler>
                    <DropdownMenu
                      onClick={(event) => {
                        event.stopPropagation()
                      }}
                    >
                      <div className='dropdown-menu-container action-table w-22 mt-2'>
                        {actions?.hasView && !hideViewBtnFn(idx, rootData) && (
                          <ButtonLink
                            to={
                              typeof hasViewBtnProps?.constructTo === 'function'
                                ? hasViewBtnProps?.constructTo(idx, rootData)
                                : 'view/' + idx
                            }
                            variant={hasViewBtnProps?.variant || 'ghost'}
                            size={hasViewBtnProps?.size || 'sm'}
                          >
                            {hasViewBtnProps?.label || 'View'}
                          </ButtonLink>
                        )}
                        {actions?.hasEdit &&
                          hasEdit &&
                          !hideEditBtnFn(idx, rootData) && (
                            <ButtonLink
                              to={
                                typeof hasEditBtnProps?.constructTo ===
                                'function'
                                  ? hasEditBtnProps?.constructTo(idx, rootData)
                                  : 'edit/' + idx
                              }
                              variant={hasEditBtnProps?.variant || 'ghost'}
                              size={hasEditBtnProps?.size || 'sm'}
                            >
                              {hasEditBtnProps?.label || 'Edit'}
                            </ButtonLink>
                          )}
                        {customActions?.(idx, value)}
                      </div>
                    </DropdownMenu>
                  </Dropdown>
                )
              },
            },
          ]
        : []),
    ],
    [columns]
  )

  const navigate = useNavigate()
  const handleClickRow = (props: TableClickRow) => {
    if (onClickRow) {
      onClickRow(props)
      return
    }
    if (!isNoActionDetail) navigate('view/' + props.id)
  }

  const { getHeaderGroups, getRowModel } = useReactTable({
    data: memoizedData,
    columns: memoizedColumns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      enableSorting: false,
    },
  })

  return (
    <table className='min-w-full divide-y divide-gray-200  bg-gray-100'>
      <thead>
        {getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <td
                key={header.id}
                className='whitespace-wrap text-wrap px-6 py-3 text-left text-sm font-bold'
              >
                {header.isPlaceholder ? null : (
                  <div
                    className='flex items-center justify-between'
                    role='presentation'
                  >
                    <span>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </span>
                  </div>
                )}
              </td>
            ))}
          </tr>
        ))}
      </thead>
      <tbody className='divide-y divide-gray-200 text-gray-800'>
        {getRowModel().rows.map((row) => {
          const idxRow = row?.original?.[keyId] || ''
          const rows = row.getVisibleCells()

          return (
            <tr
              key={row.id}
              className='cursor-pointer hover:bg-slate-100 active:bg-slate-200'
            >
              {rows.map((cell, idx) => (
                <td
                  key={cell.id}
                  role='presentation'
                  className={clsx(
                    'text-sm',
                    hasAction && idx !== rows.length - 1 && 'py-2',
                    !hasAction && 'py-2',
                    cell.column.id === 'expander' ? 'pl-4 pr-0' : 'px-6'
                  )}
                  onClick={(event) => {
                    event.preventDefault()
                    handleClickRow({
                      cell,
                      row,
                      id: idxRow,
                    })
                  }}
                  align={(cell.column.columnDef.meta as TableHeaderMeta)?.align}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

SubTable.displayName = 'SubTable'
